import type { IncidentReviewOutcome } from '../../services/incidentsApi';
import type { GroupedEvidenceReviewResolvePresentation } from './groupedRepresentativeAuditCopy';

export const OBSERVATION_DISPOSITION_PAGE_OVERLINE = 'Observation';

export const OBSERVATION_DISPOSITION_EVIDENCE_HEADING = 'Evidence for this observation';

export const OBSERVATION_DISPOSITION_WORKSPACE_HINT =
  'Use this page to spot-check or correct one danger-zone observation. Most linked observations should be dispositioned from the recurring hotspot, where bulk duplicate closure, corrective action, and report preparation happen.';

export const OBSERVATION_DISPOSITION_BACK_LABEL = 'Back to observations';

export const OBSERVATION_DISPOSITION_ACTION_HEADING = 'Disposition this observation';

export const OBSERVATION_DISPOSITION_ACTION_HELPER =
  'Choose whether this observation is a true event, duplicate, false positive, or needs more review.';

export const OBSERVATION_DISPOSITION_OWNERSHIP_HEADING = 'Establish ownership';

export const OBSERVATION_DISPOSITION_OWNERSHIP_GUIDANCE =
  'Acknowledge when someone has eyes on this observation. This claims ownership without recording a review outcome.';

export const OBSERVATION_DISPOSITION_OWNERSHIP_FOOTER =
  'Corrective action, impact measurement, and reporting stay on the recurring hotspot.';

export const OBSERVATION_DISPOSITION_CONFIRM_TRUE_EVENT_LABEL = 'Confirm true event';

export const OBSERVATION_DISPOSITION_NEEDS_MORE_REVIEW_LABEL = 'Needs more review';

export const OBSERVATION_DISPOSITION_FALSE_POSITIVE_LABEL = 'Mark false positive';

export const OBSERVATION_DISPOSITION_DUPLICATE_LABEL = 'Ignore duplicate / noise';

export const OBSERVATION_DISPOSITION_ACKNOWLEDGE_HELPER =
  'Not ready to decide? Acknowledge to claim ownership without recording an outcome.';

export const OBSERVATION_DISPOSITION_DECISION_RECORDED_TRUE_EVENT =
  'True event confirmed for this observation.';

export const OBSERVATION_DISPOSITION_DECISION_RECORDED_UPDATE_HINT =
  'This disposition is recorded on this observation. Update it below if your review changes.';

export const OBSERVATION_DISPOSITION_WORKFLOW_TITLE = 'Observation review';

export const OBSERVATION_DISPOSITION_WORKFLOW_HINT =
  'Confirm evidence, verify the clip or snapshot, and record a disposition for this observation only. Corrective action, impact measurement, and reporting stay on the recurring hotspot.';

export const OBSERVATION_EVIDENCE_CLIP_CAPTION =
  'Evidence clip captured for this observation.';

export const PATTERN_CASE_IN_PROGRESS_NOTE =
  'A recurring hotspot is open for this pattern. Use the hotspot for corrective action and reporting.';

export const REVIEW_OBSERVATION_CTA = 'REVIEW OBSERVATION';

export type HotspotOperationalSummaryMetrics = {
  observations: number;
  qualifyingEntries?: number | null;
  occupancyDurationMs?: number | null;
  forkliftCoPresenceEntries?: number | null;
};

function normalizeCount(value: number): number {
  return Math.max(0, Math.floor(Number(value) || 0));
}

function formatOccupancyDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round((Number(durationMs) || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}

export const PATTERN_EVALUATION_WINDOW_LABEL = 'PATTERN EVALUATION WINDOW';

export function formatHotspotOperationalSummary(
  metrics: HotspotOperationalSummaryMetrics,
): string {
  const observations = normalizeCount(metrics.observations);
  const parts = [
    observations === 1
      ? '1 grouped observation'
      : `${observations.toLocaleString()} grouped observations`,
  ];

  const qualifyingEntries = metrics.qualifyingEntries == null
    ? null
    : normalizeCount(metrics.qualifyingEntries);
  if (qualifyingEntries != null) {
    parts.push(
      qualifyingEntries === 1
        ? '1 qualifying entry'
        : `${qualifyingEntries.toLocaleString()} qualifying entries`,
    );
  }
  if (metrics.occupancyDurationMs != null) {
    parts.push(`${formatOccupancyDuration(metrics.occupancyDurationMs)} restricted-zone occupancy`);
  }
  if (metrics.forkliftCoPresenceEntries != null) {
    const count = normalizeCount(metrics.forkliftCoPresenceEntries);
    const rate = qualifyingEntries != null && qualifyingEntries > 0 && count <= qualifyingEntries
      ? ` (${((count / qualifyingEntries) * 100).toFixed(1)}%)`
      : '';
    parts.push(
      `forklift co-present on the same camera during ${count.toLocaleString()} ${
        count === 1 ? 'qualifying entry' : 'qualifying entries'
      }${rate}`,
    );
  }
  return parts.join(' · ');
}

export function observationCaseBridgeLine(observationCount: number, caseCount = 1): string {
  const count = Math.max(0, Number(observationCount) || 0);
  const normalizedCaseCount = Math.max(1, Number(caseCount) || 1);
  if (count <= 0) {
    const caseLabel = normalizedCaseCount === 1 ? '1 recurring hotspot' : `${normalizedCaseCount} recurring hotspots`;
    return `Open observations in the current review period belong to ${caseLabel}.`;
  }
  const observationLabel = count === 1 ? '1 open observation' : `${count} open observations`;
  const caseLabel = normalizedCaseCount === 1 ? '1 recurring hotspot' : `${normalizedCaseCount} recurring hotspots`;
  return `These ${observationLabel} are in the current review period for ${caseLabel}.`;
}

export function parentPatternCaseBannerText(
  observationCount: number,
  patternLabel?: string | null,
): string {
  const recurringRiskLabel = String(patternLabel || '').trim();
  if (recurringRiskLabel) {
    return `This observation belongs to recurring hotspot "${recurringRiskLabel}".`;
  }
  void observationCount;
  return 'This observation belongs to a recurring hotspot.';
}

export function observationParentCaseBannerSentence(
  observationCount: number,
  patternLabel?: string | null,
): string {
  const count = Math.max(0, Number(observationCount) || 0);
  const observationLabel = count === 1 ? '1 observation' : `${count} observations`;
  const recurringRiskLabel = String(patternLabel || '').trim();
  if (recurringRiskLabel) {
    return `This observation is part of recurring hotspot "${recurringRiskLabel}" (${observationLabel}). Corrective action and reporting happen on the hotspot.`;
  }
  return `This observation is part of a recurring hotspot (${observationLabel}). Corrective action and reporting happen on the hotspot.`;
}

export function openParentCaseButtonLabel(): string {
  return 'Open recurring hotspot';
}

/** Observation disposition resolve dialog copy (single observation, not representative evidence). */
export function observationDispositionResolvePresentation(
  outcome: IncidentReviewOutcome,
): GroupedEvidenceReviewResolvePresentation | null {
  switch (outcome) {
    case 'true_event':
      return {
        title: OBSERVATION_DISPOSITION_CONFIRM_TRUE_EVENT_LABEL,
        submitLabel: 'Confirm disposition',
        info:
          'Records that this observation is a true safety event. The recurring hotspot stays open for corrective action and reporting.',
      };
    case 'insufficient_evidence':
      return {
        title: OBSERVATION_DISPOSITION_NEEDS_MORE_REVIEW_LABEL,
        submitLabel: 'Save disposition',
        info:
          'Records that this observation needs more review before a final disposition can be recorded.',
      };
    case 'duplicate':
      return {
        title: OBSERVATION_DISPOSITION_DUPLICATE_LABEL,
        submitLabel: 'Confirm disposition',
        info:
          'Records that this observation is duplicate or noise and should not count toward the recurring hotspot.',
      };
    case 'false_positive':
      return {
        title: OBSERVATION_DISPOSITION_FALSE_POSITIVE_LABEL,
        submitLabel: 'Confirm disposition',
        info:
          'Records that this observation is a false positive for the recurring hotspot pattern.',
      };
    default:
      return null;
  }
}
